import './utils/axiosConfig';
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Layout from './layout/Main';
import PublicLayout from './layout/PublicLayout';
import Dashboard from './pages/Dashboard/Dashboard';
import Clients from './pages/Clients/Clients';
import Client from './pages/Client/Client';
import Calls from './pages/Calls/Calls';
import LogIn from './pages/LogIn/LogIn';
import Onboarding from './pages/Onboarding/Onboarding';
import Register from './pages/Register/Register';
import Resources from './pages/Resources/Resources';
import LessonOfList from './pages/ListOfLesson/ListOfLesson';
import LessonListMiniCourse from './components/LessonListMiniCourse/LessonListMiniCourse';
import Reports from './pages/Reports/Reports';
import NewClientForm from './pages/NewClientForm/NewClientForm'
import ImportClients from './pages/ImportClients/ImportClients';
import EditClientForm from './pages/EditClientForm/EditClientForm'
import Legal from './pages/Legal/Legal';
import Privacy from './pages/Privacy/Privacy';
import TermsOfUse from './pages/TermsOfUse/TermsOfUse';
import Surveys from './pages/Surveys/Surveys';
import ZoomSupport from './pages/ZoomSupport/ZoomSupport';
import ZoomDocumentation from './pages/ZoomDocumentation/ZoomDocumentation';
import KnowledgeBase from './pages/KnowledgeBase/KnowledgeBase';
import Auth from './utils/auth';
import LogRocket from 'logrocket';
import RetentionCalculator from "./pages/RetentionCalculator/RetentionCalculator";
import PublicRetentionCalculator from "./pages/RetentionCalculator/PublicRetentionCalculator";
import AdminPanel from './pages/AdminPanel/AdminPanel';
import BiAgent from './pages/BiAgent/BiAgent';
import EditProfile from './pages/EditProfile/EditProfile';
import ChangePasswordModal from './components/ChangePasswordModal/ChangePasswordModal';
import BulkUpdateModal from './components/ClientList/BulkUpdateModal';
import SuperAdminDashboard from './pages/SuperAdminDashboard/SuperAdminDashboard';
import ZapierConsent from './components/ComponentsIntegration/Integrations/ZapierIntegrations/ZapierConsent';
import ZoomParticipants from './pages/ZoomParticipants/ZoomParticipants';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
LogRocket.init('iitrjy/clientbloom');

// App.js o routes.jsx

const SUPER_ADMIN_WORKSPACE_IDS = [6, 41];

const PrivateRoute = ({ element, requireAdmin = false, requireSuperAdmin = false }) => {
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(null);
    const [isAuthorized, setIsAuthorized] = useState(null);
    const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const checkAuth = async () => {
            const token = Auth.getToken();
            if (!token) {
                setIsAuthenticated(false);
                setIsAuthorized(false);
                setLoading(false);
                return;
            }
            const authenticated = await Auth.isAuthenticated();
            const user = Auth.getUser();

            if (!authenticated) {
                setIsAuthenticated(false);
                setIsAuthorized(false);
                setLoading(false);
                return;
            }

            setIsAuthenticated(authenticated);

            if (requireSuperAdmin && !SUPER_ADMIN_WORKSPACE_IDS.includes(Number(Auth.getWorkspaceId()))) {
                setIsAuthorized(false);
            } else if (requireAdmin && !user?.admin) {
                setIsAuthorized(false);
            } else {
                setIsAuthorized(true);
            }
            const passwordModalShown = localStorage.getItem('passwordModalShown');
            if (user?.must_change_password && !passwordModalShown) {
                setShowPasswordChangeModal(true);
                localStorage.setItem('passwordModalShown', 'true');
            }

            setLoading(false);
        };
        checkAuth();
    }, [requireAdmin, requireSuperAdmin]);

    if (isAuthenticated === null || isAuthorized === null) {
        return <div>Loading...</div>;
    }

    if (!isAuthenticated) {
        const isZapierConsent = (location.pathname || "").startsWith("/zapier-Consent");
        return (
            <Navigate
                to="/login"
                replace
                state={isZapierConsent ? { from: location.pathname + location.search } : undefined}
            />
        );
    }

    if (!isAuthorized) {
        return <Navigate to="/" replace />;
    }

    return (
        <>
            {element}
            <ChangePasswordModal
                isOpen={showPasswordChangeModal}
                onClose={() => setShowPasswordChangeModal(false)}
            />
        </>
    );
};

function App() {
    return (
        <Router>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
            <Routes>
                {/* Standalone Public Routes - No Layout */}
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/free-retention-calculator" element={<PublicRetentionCalculator />} />
                <Route path="/zoom-documentation" element={<ZoomDocumentation />} />
                <Route
                    path="/zapier-Consent"
                    element={
                        <PrivateRoute element={<ZapierConsent />} requireAdmin />
                    }
                />
                
                {/* Public Layout Routes */}
                <Route element={<PublicLayout />}>
                    <Route path="/login" element={<LogIn />} />
                    <Route path="/admin-login" element={<LogIn admin />} />
                    <Route path="/register" element={<Navigate to="/onboarding" replace />} />
                    <Route path="/retention-calculator" element={<RetentionCalculator />} />
                    <Route path="/privacy-policy" element={<Privacy />} />
                    <Route path="/zoom-support" element={<ZoomSupport />} />
                    <Route path="/terms-of-use" element={<TermsOfUse />} />
                </Route>

                {/* Main Layout Routes */}
                <Route element={<Layout />}>
                    <Route
                        path="/"
                        element={
                            <PrivateRoute element={<Dashboard />} />
                        }
                    />
                    <Route
                        path="/clients"
                        element={
                            <PrivateRoute element={<Clients />} />
                        }
                    />
                    <Route
                        path="/bi-agent"
                        element={
                            <PrivateRoute element={<BiAgent />} />
                        }
                    />
                    <Route
                        path="/client/:clientId"
                        element={
                            <PrivateRoute element={<Client />} />
                        }
                    />
                    <Route
                        path="/calls/:clientId"
                        element={
                            <PrivateRoute element={<Calls />} />
                        }
                    />
                    <Route
                        path="/calls/:clientId/:callScoreId"
                        element={
                            <PrivateRoute element={<Calls />} />
                        }
                    />
                    <Route
                        path="/edit-client-form"
                        element={
                            <PrivateRoute element={<EditClientForm />} />
                        }
                    />
                    <Route
                        path="/Resources"
                        element={
                            <PrivateRoute element={<Resources />} />
                        }
                    />
                    <Route
                        path="/resource-retention-calculator"
                        element={
                            <PrivateRoute element={<RetentionCalculator />} />
                        }
                    />
                    <Route
                        path="/knowledge-base"
                        element={
                            <PrivateRoute element={<KnowledgeBase />} />
                        }
                    />
                    <Route
                        path="/new-client-form"
                        element={
                            <PrivateRoute element={<NewClientForm />} />
                        }
                    />
                    <Route
                        path="/import-client"
                        element={
                            <PrivateRoute element={<ImportClients />} />
                        }
                    />
                    <Route
                        path="/surveys"
                        element={
                            <PrivateRoute element={<Surveys />} />
                        }
                    />
                    <Route
                        path="/reports"
                        element={
                            <PrivateRoute element={<Reports />} />
                        }
                    />
                    <Route
                        path="/admin-panel"
                        element={
                            <PrivateRoute element={<AdminPanel />} requireAdmin />
                        }
                    />
                    <Route
                        path="/admin-panel/integrations"
                        element={
                            <PrivateRoute element={<AdminPanel />} requireAdmin />
                        }
                    />
                    <Route
                        path="/admin-panel/integrations/:integrationId"
                        element={
                            <PrivateRoute element={<AdminPanel />} requireAdmin />
                        }
                    />
                    <Route
                        path="/zoom-meeting-logs"
                        element={
                            <PrivateRoute element={<ZoomParticipants />} requireAdmin />
                        }
                    />
                    <Route
                        path="/lesson-list/:lessonId"
                        element={
                            <PrivateRoute element={<LessonOfList />} />
                        }
                    />
                    <Route
                        path="/lesson-list-mini-course/:lessonId"
                        element={
                            <PrivateRoute element={<LessonListMiniCourse />} />
                        }
                    />
                    <Route
                        path="/legal"
                        element={
                            <PrivateRoute element={<Legal />} />
                        }
                    />
                    <Route
                        path="/edit-profile"
                        element={
                            <PrivateRoute element={<EditProfile />} />
                        }
                    />
                    <Route
                        path="/bulk-update-clients"
                        element={
                            <PrivateRoute element={<BulkUpdateModal />} />
                        }
                    />
                    <Route
                        path="/super-admin"
                        element={
                            <PrivateRoute element={<SuperAdminDashboard />} requireSuperAdmin />
                        }
                    />
                </Route>
            </Routes>
        </Router>
    );
}

export default App;
